// ─── useDataOwnerId — resolve the studio's data anchor ──────────────────────
//
// Returns the user_id that should be used for `.eq('user_id', ...)` filters
// in queries that read studio-shared data (leads, clients, events, quotes,
// contracts, invoices, payments, etc.).
//
// Why this exists: when a planner invites an assistant via Settings → Team,
// the invitee accepts and their profiles row gets `owner_id = planner.id`
// (see src/pages/auth/JoinTeam.jsx, 20260402_team_rbac.sql). Every shared-
// data table stores user_id = the planner's id. RLS policies (rewritten by
// 20260518_studio_shared_data.sql) allow team members to see the planner's
// rows. But every frontend query does `.eq('user_id', user.id)` — which for
// a member is THEIR id, not the planner's. So even with RLS opening
// visibility, the .eq filter excludes the rows that just became visible.
//
// This hook is the canonical fix at the query-layer:
//
//   const ownerId = useDataOwnerId()
//   ...
//   const { data } = await supabase.from('leads')
//     .select('*')
//     .eq('user_id', ownerId)   // ← was .eq('user_id', user.id)
//
// For owner accounts (profile.owner_id is null), the hook returns user.id —
// existing behavior, no regression.
//
// For member accounts (profile.owner_id is set), the hook returns the
// owner_id — queries pick up the studio's data.
//
// For INSERTs the same idiom applies: members should write rows with
// user_id = ownerId so the data settles under the studio's anchor and the
// owner sees member-created rows natively (rather than via the more
// expensive bidirectional RLS path).
//
// Edge cases:
//   - profile not loaded yet → returns null. Callers should guard:
//       if (!ownerId) return  // wait until profile resolves
//   - unauthenticated → no user, no profile → returns null
//   - team owner → profile.owner_id is null → returns user.id

import { useAuthContext } from '../context/AuthContext'

export function useDataOwnerId() {
  const { user, profile } = useAuthContext()
  if (!user) return null
  // profile may be undefined while auth is loading; in that window, fall
  // back to user.id so the query at least targets the right account for
  // owner-only studios (the dominant case at beta scale). Once profile
  // resolves with owner_id set, the next render swaps in the studio
  // anchor.
  return profile?.owner_id || user.id
}
